import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Copy, Check, Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function CopyableRow({ label, value, highlight, t }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {}
  };
  return (
    <div
      onClick={handleCopy}
      className={`flex items-center justify-between gap-3 py-3 px-4 rounded-lg cursor-pointer transition-colors active:bg-blue-100
        ${highlight ? 'bg-amber-50 hover:bg-amber-100 border border-amber-200' : 'bg-white hover:bg-blue-50 border border-gray-200'}`}
      data-testid={`pay-row-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">{label}</div>
        <div className={`font-mono text-sm sm:text-base break-all ${highlight ? 'text-amber-900 font-bold' : 'text-gray-900'}`}>
          {value}
        </div>
      </div>
      {copied ? (
        <span className="flex items-center text-emerald-600 text-xs font-medium flex-shrink-0">
          <Check className="h-4 w-4 mr-1" />{t('publicPayment.copied')}
        </span>
      ) : (
        <Copy className="h-4 w-4 text-gray-400 flex-shrink-0" />
      )}
    </div>
  );
}

export default function PublicPayment() {
  const { t } = useTranslation();
  const { invoice } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/payment/${invoice}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || t('publicPayment.invoiceNotFound')))
      .finally(() => setLoading(false));
  }, [invoice, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="max-w-md p-8 text-center">
          <h1 className="text-2xl font-bold mb-2">{t('publicPayment.invoiceNotFound')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="text-blue-600 hover:underline">{t('publicPayment.backToSite')}</Link>
        </Card>
      </div>
    );
  }

  const { bank, amount_cve, reference, plan_name, invoice_number, status } = data;
  const isPaid = status === 'approved';
  const isRejected = status === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4" data-testid="public-payment-page">
      <div className="max-w-md mx-auto">
        {/* Brand header */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <h1 className="text-4xl font-bold">
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Trabadju
              </span>
            </h1>
          </Link>
          <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">{t('publicPayment.securePayment')}</p>
        </div>

        <Card className="overflow-hidden shadow-xl">
          {/* Status banner */}
          {isPaid && (
            <Alert className="rounded-none border-0 border-b border-emerald-200 bg-emerald-50">
              <AlertDescription className="text-emerald-900 font-medium flex items-center">
                <ShieldCheck className="h-4 w-4 mr-2" />
                {t('publicPayment.alreadyPaid')}
              </AlertDescription>
            </Alert>
          )}
          {isRejected && (
            <Alert variant="destructive" className="rounded-none border-0 border-b">
              <AlertDescription>{t('publicPayment.rejected')}</AlertDescription>
            </Alert>
          )}

          {/* Amount banner */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 text-white">
            <div className="flex items-center justify-between mb-1">
              <div className="text-blue-200 text-xs uppercase tracking-wider">{t('publicPayment.invoice')}</div>
              <div className="font-mono text-sm">{invoice_number}</div>
            </div>
            <div className="text-blue-100 text-sm mb-2">{plan_name}</div>
            <div className="text-4xl font-bold">{amount_cve?.toLocaleString('pt-PT')} <span className="text-xl text-blue-200">CVE</span></div>
          </div>

          {/* Instructions */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h2 className="font-semibold text-gray-900">{t('publicPayment.transferDetails')}</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">{t('publicPayment.tapToCopy')}</p>

            <div className="space-y-2">
              <CopyableRow t={t} label={t('publicPayment.holder')} value={bank.holder} />
              {bank.bank_name && <CopyableRow t={t} label={t('publicPayment.bank')} value={bank.bank_name} />}
              {bank.account_number && <CopyableRow t={t} label={t('publicPayment.accountNumber')} value={bank.account_number} />}
              {bank.nib && <CopyableRow t={t} label="NIB" value={bank.nib} />}
              {bank.iban && <CopyableRow t={t} label="IBAN" value={bank.iban} />}
              {bank.swift && <CopyableRow t={t} label="SWIFT/BIC" value={bank.swift} />}
              <CopyableRow t={t} label={t('publicPayment.amount')} value={`${amount_cve} CVE`} />
              <CopyableRow t={t} label={t('publicPayment.reference')} value={reference} highlight />
            </div>

            <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              <strong>{t('publicPayment.important')}:</strong> {t('publicPayment.referenceWarning')} <code className="font-mono font-bold">{reference}</code> {t('publicPayment.referenceWarningSuffix')}
            </div>

            {!isPaid && !isRejected && (
              <p className="mt-4 text-center text-sm text-gray-600">
                {t('publicPayment.afterTransfer')}
              </p>
            )}
          </div>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          © {new Date().getFullYear()} Tavares Groupe · NIF 213407175
        </p>
      </div>
    </div>
  );
}
